import React, { Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './components/shared/notifications/NotificationSystem';
import { ToastProvider } from './components/shared/notifications/ToastProvider';
import { AppContextProvider } from './contexts/AppContext';
import { SocketProvider } from './contexts/SocketContext';
import { PermissionProvider } from './contexts/AdaptivePermissionContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { OfflineToastProvider } from './contexts/OfflineToastContext';
import { SiteProvider, useSite } from './contexts/SiteContext';
import OfflineToastHandler from './components/shared/notifications/OfflineToastHandler';
import PwaUpdatePrompt from './components/shared/app/PwaUpdatePrompt';
import SplashScreen from './components/ui/SplashScreen';
import Login from './login';
import AdminRHProtectedRoute from './components/admin-rh/routes/AdminRHProtectedRoute';
import ConfigurationWrapper from './components/ui/ConfigurationWrapper';
import PageContentSkeleton from './components/common/PageContentSkeleton';
import EmployeeProtectedRoute from './components/web-app-secura/routes/EmployeeProtectedRoute';
import WebHostGuard from './components/shared/routes/WebHostGuard';
import SidebarAdminRH from './components/admin-rh/layout/SidebarAdminRH';
import NavbarAdminRH from './components/admin-rh/layout/NavbarAdminRH';
import AdminProtectedRoute from './pages/admin-panel/layout/AdminProtectedRoute';
import AdminLayout from './pages/admin-panel/layout/AdminLayout';
import lazyPage from './utils/lazyPage';
// Pages chargées à la demande : chaque espace (RH, admin, employé) n'est téléchargé qu'à la première visite.
// RH
const CongesAdminRH = lazyPage(() => import('./pages/admin-rh/leaves/CongesAdminRH.jsx'));
const Dashboard = lazyPage(() => import('./pages/admin-rh/dashboard/Dashboard.jsx'));
const Ressources = lazyPage(() => import('./pages/admin-rh/resources/Ressources.jsx'));
const Horaires = lazyPage(() => import('./pages/admin-rh/schedule/Horaires.jsx'));
const Pointages = lazyPage(() => import('./pages/admin-rh/attendance/Pointages.jsx'));
const Parametres = lazyPage(() => import('./pages/admin-rh/settings/Parametres.jsx'));
const Configuration = lazyPage(() => import('./pages/admin-rh/settings/Configuration.jsx'));
const FieldVisitsRH = lazyPage(() => import('./pages/admin-rh/field/FieldVisitsRH.jsx'));
const FieldVisitsInterSiteRH = lazyPage(() => import('./pages/admin-rh/field/FieldVisitsInterSiteRH.jsx'));
const Paie = lazyPage(() => import('./pages/admin-rh/payroll/Paie.jsx'));
const ComptabilitePaie = lazyPage(() => import('./pages/admin-rh/payroll/ComptabilitePaie.jsx'));
const AdminRHPlusManagement = lazyPage(() => import('./pages/admin-rh/admin-rh-plus/AdminRHPlusManagement.jsx'));
const ActivateAccount = lazyPage(() => import('./pages/admin-rh/admin-rh-plus/ActivateAccount.jsx'));
const AdminRHPlusInvitation = lazyPage(() => import('./pages/admin-rh/admin-rh-plus/AdminRHPlusInvitation.jsx'));
// Administration Secura
const AdminDashboard = lazyPage(() => import('./pages/admin-panel/dashboard/AdminDashboard.jsx'));
const AdminUsers = lazyPage(() => import('./pages/admin-panel/users/AdminUsers.jsx'));
const AdminCompanies = lazyPage(() => import('./pages/admin-panel/organizations/AdminCompanies.jsx'));
const AdminPanelManagement = lazyPage(() => import('./pages/admin-panel/panels/AdminPanelManagement.jsx'));
const AdminFactures = lazyPage(() => import('./pages/admin-panel/billing/AdminFactures.jsx'));
const AdminHistorique = lazyPage(() => import('./pages/admin-panel/reports/AdminHistorique.jsx'));
const AdminPanelInvitation = lazyPage(() => import('./pages/admin-panel/auth/AdminPanelInvitation.jsx'));
const AdminWhatsApp = lazyPage(() => import('./pages/admin-panel/integrations/AdminWhatsApp.jsx'));
const AdminLigdicash = lazyPage(() => import('./pages/admin-panel/integrations/AdminLigdicash.jsx'));
// Employé
const Home = lazyPage(() => import('./pages/employee/home/Home.jsx'));
const EmployeeDashboard = lazyPage(() => import('./pages/employee/dashboard/Dashboard.jsx'));
const PointageEntree = lazyPage(() => import('./pages/employee/attendance/PointageEntree.jsx'));
const PointageSortie = lazyPage(() => import('./pages/employee/attendance/PointageSortie.jsx'));
const PointageEnregistreeEntre = lazyPage(() => import('./pages/employee/attendance/PointageEnregistreeEntre.jsx'));
const PointageEnregistreeSortie = lazyPage(() => import('./pages/employee/attendance/PointageEnregistreeSortie.jsx'));
const Carte = lazyPage(() => import('./pages/employee/profile/Carte.jsx'));
const PointagesEmployee = lazyPage(() => import('./pages/employee/attendance/Pointages.jsx'));
const Rapport = lazyPage(() => import('./pages/employee/reports/Rapport.jsx'));
const Profil = lazyPage(() => import('./pages/web-app-secura/profile/Profil.jsx'));
const CongesEmployee = lazyPage(() => import('./pages/employee/leaves/CongesEmployee.jsx'));
const Absences = lazyPage(() => import('./pages/employee/leaves/Absences.jsx'));
const ValidationConges = lazyPage(() => import('./pages/employee/leaves/ValidationConges.jsx'));
const CreateLeaveRequest = lazyPage(() => import('./pages/employee/leaves/CreateLeaveRequest.jsx'));
const SoldeConge = lazyPage(() => import('./pages/employee/leaves/SoldeConge.jsx'));
const Projets = lazyPage(() => import('./pages/web-app-secura/projects/Projets.jsx'));
const ProjectTasks = lazyPage(() => import('./pages/web-app-secura/projects/ProjectTasks.jsx'));
const ProjectDetail = lazyPage(() => import('./pages/web-app-secura/projects/ProjectDetail.jsx'));
const CreateProject = lazyPage(() => import('./pages/web-app-secura/projects/CreateProject.jsx'));
const ModifyProject = lazyPage(() => import('./pages/web-app-secura/projects/ModifyProject.jsx'));
const CreateTask = lazyPage(() => import('./pages/web-app-secura/projects/CreateTask.jsx'));
const ModifyTask = lazyPage(() => import('./pages/web-app-secura/projects/ModifyTask.jsx'));
const TaskDetail = lazyPage(() => import('./pages/web-app-secura/projects/TaskDetail.jsx'));
const Horaire = lazyPage(() => import('./pages/web-app-secura/schedule/Horaire.jsx'));
const Messagerie = lazyPage(() => import('./pages/web-app-secura/projects/Messagerie.jsx'));
const LocationActivation = lazyPage(() => import('./pages/employee/auth/LocationActivation.jsx'));
const QuickCheckin = lazyPage(() => import('./pages/employee/attendance/QuickCheckin.jsx'));
const QuickCheckinFromShortUrl = lazyPage(() => import('./pages/employee/attendance/QuickCheckinFromShortUrl.jsx'));
const Register = lazyPage(() => import('./pages/employee/auth/Register.jsx'));
const ResetPasswordEmployee = lazyPage(() => import('./pages/employee/auth/ResetPassword.jsx'));
const LoginWebauthn = lazyPage(() => import('./pages/web-app-secura/auth/Login.jsx'));
const PasswordFallback = lazyPage(() => import('./pages/employee/auth/PasswordFallback.jsx'));
const PointageTerrainPage = lazyPage(() => import('./pages/employee/field/PointageTerrainPage.jsx'));
const PointageInterSitePage = lazyPage(() => import('./pages/employee/field/PointageInterSitePage.jsx'));
const MesBulletins = lazyPage(() => import('./pages/employee/payroll/MesBulletins.jsx'));
// Site public (accueil, tarifs, contact, inscription entreprise)
const Landing = lazyPage(() => import('./pages/public/Landing.jsx'));
const Pricing = lazyPage(() => import('./pages/public/Pricing.jsx'));
const Contact = lazyPage(() => import('./pages/public/Contact.jsx'));
const CompanySignup = lazyPage(() => import('./pages/public/CompanySignup.jsx'));
const Confidentialite = lazyPage(() => import('./pages/public/Confidentialite.jsx'));
const PaymentResult = lazyPage(() => import('./pages/public/PaymentResult.jsx'));

/** Pages du site public : affichées sans l'écran de démarrage de l'application. */
const PUBLIC_PATHS = ['/', '/pricing', '/tarifs', '/contact', '/creer-entreprise', '/paiement/retour', '/paiement/annule'];
const isPublicPath = (pathname) => PUBLIC_PATHS.includes(pathname.replace(/\/+$/, '') || '/');

/** App installée (PWA) : ouverte en plein écran, elle va directement à la connexion, pas au site. */
const isStandaloneApp = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

/** « / » : site public sur app-secura.com ; connexion dans l'app installée (web.* est géré par WebHostGuard). */
const HomeRoute = () => (isStandaloneApp() ? <Navigate to="/login" replace /> : <Landing />);

/** Pendant le téléchargement d'une page (première visite) : indicateur discret. */
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center bg-white" role="status" aria-label="Chargement">
    <span className="h-8 w-8 rounded-full border-[3px] border-gray-200 border-t-[#0026E2] animate-spin" />
  </div>
);

// Pages probables préchargées pendant l'écran de démarrage (3,5 s), pour qu'elles s'affichent sans attente
const prefetchLikelyPages = () => {
  const path = window.location.pathname;
  if (window.location.hostname.startsWith('web.')) {
    import('./pages/web-app-secura/projects/Projets.jsx');
    import('./pages/web-app-secura/projects/ProjectDetail.jsx');
  } else if (path.startsWith('/admin')) {
    import('./pages/admin-panel/dashboard/AdminDashboard.jsx');
  } else {
    import('./pages/employee/home/Home.jsx');
    import('./pages/admin-rh/dashboard/Dashboard.jsx');
  }
};

// Composant interne pour utiliser le hook useSidebar
const LayoutContent = ({ children }) => {
  const { isCollapsed, isOpen, toggleSidebar, toggleSidebarOpen, closeSidebar } = useSidebar();
  const { siteChanging } = useSite();
  const location = useLocation();
  const [routeChanging, setRouteChanging] = useState(false);
  const routeTimerRef = useRef(null);
  const prevPathRef = useRef(location.pathname);

  // Skeleton de navigation: au moins 2 secondes à chaque changement de route
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setRouteChanging(true);
      if (routeTimerRef.current) clearTimeout(routeTimerRef.current);
      routeTimerRef.current = setTimeout(() => {
        setRouteChanging(false);
      }, 1000);
    }
    return () => {
      if (routeTimerRef.current) clearTimeout(routeTimerRef.current);
    };
  }, [location.pathname]);

  const getVariantForPath = (pathname) => {
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/parametres')) return 'form';
    if (pathname.startsWith('/pointages')) return 'table';
    if (pathname.startsWith('/ressources') || pathname.startsWith('/employes') || pathname.startsWith('/departements')) return 'table';
    return 'page';
  };

  const handleMenuClick = () => {
    toggleSidebarOpen();
    toggleSidebar();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar */}
      <SidebarAdminRH 
        isOpen={isOpen}
        onClose={closeSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleSidebar}
        siteChanging={siteChanging}
      />

      {/* Main content avec ConfigurationWrapper */}
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <ConfigurationWrapper>
          {/* Navbar avec skeleton intégré */}
          <NavbarAdminRH 
            onMenuClick={handleMenuClick}
            isCollapsed={isCollapsed}
            siteChanging={siteChanging}
          />

          {/* Page content avec skeleton intégré */}
          <main className="w-full min-h-screen">
            <div className="w-full">
              <PageContentSkeleton
                isLoading={siteChanging || routeChanging}
                variant={getVariantForPath(location.pathname)}
              >
                {children}
              </PageContentSkeleton>
            </div>
          </main>
        </ConfigurationWrapper>
      </div>
    </div>
  );
};

// Layout wrapper pour AdminRH avec Sidebar et Navbar
const LayoutAdminRHWrapper = ({ children }) => {
  return (
    <LayoutContent>
      {children}
    </LayoutContent>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(
    () => !(isPublicPath(window.location.pathname) && !isStandaloneApp() && !window.location.hostname.startsWith('web.'))
  );

  useEffect(() => {
    if (showSplash) prefetchLikelyPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <ToastProvider />
            <AppContextProvider>
              <PermissionProvider>
                <OnboardingProvider>
                  <SiteProvider>
                    <SidebarProvider>
                      <OfflineToastProvider>
                        <SocketProvider>
                        <OfflineToastHandler />
                        <PwaUpdatePrompt />
                    {showSplash ? (
                      <SplashScreen onComplete={() => setShowSplash(false)} />
                    ) : (
                      <>
                      <WebHostGuard />
                      <Suspense fallback={<PageLoading />}>
                      <Routes>
                        {/* Site public : accueil, tarifs, inscription entreprise avec l'offre choisie */}
                        <Route path="/" element={<HomeRoute />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/tarifs" element={<Navigate to="/pricing" replace />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/creer-entreprise" element={<CompanySignup />} />
                        <Route path="/confidentialite" element={<Confidentialite />} />
                        <Route path="/privacy" element={<Navigate to="/confidentialite" replace />} />
                        <Route path="/paiement/retour" element={<PaymentResult mode="return" />} />
                        <Route path="/paiement/annule" element={<PaymentResult mode="cancel" />} />
                        
                        {/* Route de login */}
                        <Route path="/login" element={<Login />} />
                        
                        {/* Routes AdminRH */}
                        <Route 
                          path="/dashboard" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Dashboard />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/ressources" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Ressources />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        {/* Route /employes - redirige vers /ressources avec section employés */}
                        <Route 
                          path="/employes" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Ressources defaultSection="employees" />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        {/* Route /departements - redirige vers /ressources avec section départements */}
                        <Route 
                          path="/departements" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Ressources defaultSection="departements" />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/admin-rh-plus" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <AdminRHPlusManagement />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/horaires" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Horaires />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/pointages" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Pointages />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />

                        <Route
                          path="/pointage-terrain-admin"
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <FieldVisitsRH prospectionMode="terrain" />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          }
                        />
                        <Route
                          path="/pointage-inter-site-admin"
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <FieldVisitsInterSiteRH />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          }
                        />
                        
                        <Route 
                          path="/conges" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <CongesAdminRH />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />

                        <Route
                          path="/paie"
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Paie />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          }
                        />

                        <Route
                          path="/comptabilite-paie"
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <ComptabilitePaie />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          }
                        />
                        
                        <Route 
                          path="/parametres" 
                          element={
                            <AdminRHProtectedRoute>
                              <LayoutAdminRHWrapper>
                                <Parametres />
                              </LayoutAdminRHWrapper>
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/configuration" 
                          element={
                            <AdminRHProtectedRoute>
                              <Configuration />
                            </AdminRHProtectedRoute>
                          } 
                        />
                        
                        {/* Routes AdminPanel */}
                        <Route 
                          path="/adminpanel" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminDashboard />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/adminpanel/users" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminUsers />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/adminpanel/companies" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminCompanies />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/adminpanel/admin-panels" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminPanelManagement />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/adminpanel/factures" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminFactures />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/adminpanel/history" 
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminHistorique />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          } 
                        />

                        <Route
                          path="/adminpanel/whatsapp"
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminWhatsApp />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          }
                        />

                        <Route
                          path="/adminpanel/paiements"
                          element={
                            <AdminProtectedRoute>
                              <AdminLayout>
                                <AdminLigdicash />
                              </AdminLayout>
                            </AdminProtectedRoute>
                          }
                        />
                        
                        {/* Routes Employee */}
                        <Route 
                          path="/home" 
                          element={
                            <EmployeeProtectedRoute>
                              <Home />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/employeeDashboard" 
                          element={
                            <EmployeeProtectedRoute>
                              <EmployeeDashboard />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de pointage */}
                        <Route 
                          path="/pointageEntree" 
                          element={
                            <EmployeeProtectedRoute>
                              <PointageEntree />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/pointageSortie" 
                          element={
                            <EmployeeProtectedRoute>
                              <PointageSortie />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/pointageEnregistreeEntre" 
                          element={
                            <EmployeeProtectedRoute>
                              <PointageEnregistreeEntre />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/pointageEnregistreeSortie" 
                          element={
                            <EmployeeProtectedRoute>
                              <PointageEnregistreeSortie />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de carte et localisation */}
                        <Route 
                          path="/carte" 
                          element={
                            <EmployeeProtectedRoute>
                              <Carte />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/activate-location" 
                          element={
                            <EmployeeProtectedRoute>
                              <LocationActivation />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de pointages et rapports */}
                        <Route
                          path="/pointage-terrain"
                          element={
                            <EmployeeProtectedRoute>
                              <PointageTerrainPage />
                            </EmployeeProtectedRoute>
                          }
                        />
                        <Route
                          path="/pointage-terrain/visites"
                          element={
                            <EmployeeProtectedRoute>
                              <PointageTerrainPage />
                            </EmployeeProtectedRoute>
                          }
                        />
                        <Route
                          path="/pointage-inter-site"
                          element={
                            <EmployeeProtectedRoute>
                              <PointageInterSitePage />
                            </EmployeeProtectedRoute>
                          }
                        />
                        <Route
                          path="/pointage-inter-site/visites"
                          element={
                            <EmployeeProtectedRoute>
                              <PointageInterSitePage />
                            </EmployeeProtectedRoute>
                          }
                        />

                        <Route 
                          path="/mes-pointages" 
                          element={
                            <EmployeeProtectedRoute>
                              <PointagesEmployee />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/mes-rapports" 
                          element={
                            <EmployeeProtectedRoute>
                              <Rapport />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/profil" 
                          element={
                            <EmployeeProtectedRoute>
                              <Profil />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de congés */}
                        <Route 
                          path="/mes-conges" 
                          element={
                            <EmployeeProtectedRoute>
                              <CongesEmployee />
                            </EmployeeProtectedRoute>
                          } 
                        />

                        <Route
                          path="/mes-bulletins"
                          element={
                            <EmployeeProtectedRoute>
                              <MesBulletins />
                            </EmployeeProtectedRoute>
                          }
                        />
                        
                        <Route 
                          path="/conges" 
                          element={
                            <EmployeeProtectedRoute>
                              <CongesEmployee />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/validation-conges" 
                          element={
                            <EmployeeProtectedRoute>
                              <ValidationConges />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/create-leave-request" 
                          element={
                            <EmployeeProtectedRoute>
                              <CreateLeaveRequest />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        <Route 
                          path="/solde-conge" 
                          element={
                            <EmployeeProtectedRoute>
                              <SoldeConge />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes d'absences */}
                        <Route 
                          path="/mes-absences" 
                          element={
                            <EmployeeProtectedRoute>
                              <Absences />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de projets */}
                        <Route 
                          path="/projets" 
                          element={
                            <EmployeeProtectedRoute>
                              <Projets />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/projets/create" 
                          element={
                            <EmployeeProtectedRoute>
                              <CreateProject />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/projets/:projetId/modify" 
                          element={
                            <EmployeeProtectedRoute>
                              <ModifyProject />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/projets/:projetId" 
                          element={
                            <EmployeeProtectedRoute>
                              <ProjectDetail />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/projets/:projetId/tasks" 
                          element={
                            <EmployeeProtectedRoute>
                              <ProjectTasks />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de tâches */}
                        <Route 
                          path="/tasks" 
                          element={
                            <EmployeeProtectedRoute>
                              <Projets />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/tasks/create" 
                          element={
                            <EmployeeProtectedRoute>
                              <CreateTask />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/tasks/modify/:taskId" 
                          element={
                            <EmployeeProtectedRoute>
                              <ModifyTask />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/tasks/:taskId" 
                          element={
                            <EmployeeProtectedRoute>
                              <TaskDetail />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes d'horaires et messagerie */}
                        <Route 
                          path="/horaire" 
                          element={
                            <EmployeeProtectedRoute>
                              <Horaire />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        <Route 
                          path="/messagerie" 
                          element={
                            <EmployeeProtectedRoute>
                              <Messagerie />
                            </EmployeeProtectedRoute>
                          } 
                        />
                        
                        {/* Routes de quick check-in */}
                        <Route 
                          path="/quick-checkin/:employeeId/:entrepriseId" 
                          element={
                            <QuickCheckin />
                          } 
                        />
                        
                        <Route 
                          path="/quick-checkin/:shortCode" 
                          element={
                            <QuickCheckinFromShortUrl />
                          } 
                        />
                        
                        {/* Routes publiques Employee (authentification) */}
                        <Route 
                          path="/register" 
                          element={<Register />} 
                        />
                        
                        <Route 
                          path="/reset-password" 
                          element={<ResetPasswordEmployee />} 
                        />
                        
                        <Route 
                          path="/login-webauthn" 
                          element={<LoginWebauthn />} 
                        />
                        
                        <Route 
                          path="/password-fallback" 
                          element={<PasswordFallback />} 
                        />
                        
                        {/* Routes publiques d'activation et d'invitation (sans authentification) */}
                        <Route 
                          path="/activate-account" 
                          element={<ActivateAccount />} 
                        />
                        
                        <Route 
                          path="/admin-rh-plus/invitation" 
                          element={<AdminRHPlusInvitation />} 
                        />
                        
                        <Route 
                          path="/admin-panel/invitation" 
                          element={<AdminPanelInvitation />} 
                        />
                        
                        {/* Route par défaut - redirige vers login */}
                        <Route path="*" element={<Navigate to="/login" replace />} />
                      </Routes>
                      </Suspense>
                      </>
                    )}
                        </SocketProvider>
                    </OfflineToastProvider>
                  </SidebarProvider>
                  </SiteProvider>
                </OnboardingProvider>
              </PermissionProvider>
            </AppContextProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
